import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Copy, Check, BookOpen, Radio, Zap, Shield, Brain, Terminal, Settings, Code, Database, Users, Layers, Clock, Bell, FileCode, GitBranch, ArrowRight } from 'lucide-react';
import './Docs.css';

const sections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    items: [
      { id: 'introduction', title: 'Introduction' },
      { id: 'quickstart', title: 'Quick Start' },
    ]
  },
  {
    id: 'agent-setup',
    title: 'Agent Setup',
    items: [
      { id: 'custom-agent-guide', title: 'Build a Custom Agent' },
      { id: 'creating-agents', title: 'Creating an Agent' },
      { id: 'identity', title: 'Identity & Purpose' },
      { id: 'teaching', title: 'Behavior & Teaching' },
      { id: 'escalation', title: 'Escalation' },
      { id: 'environment', title: 'Environment' },
    ]
  },
  {
    id: 'listeners',
    title: 'Listeners',
    items: [
      { id: 'listeners-overview', title: 'How Listeners Work' },
      { id: 'listener-format', title: 'Event Format' },
      { id: 'listener-types', title: 'Polling vs. Webhook' },
      { id: 'listener-credentials', title: 'Credentials' },
      { id: 'threading', title: 'Conversation Threading' },
    ]
  },
  {
    id: 'skills',
    title: 'Skills',
    items: [
      { id: 'skills-overview', title: 'How Skills Work' },
      { id: 'skill-format', title: 'Skill Format' },
      { id: 'skill-execution', title: 'Execution & Limits' },
      { id: 'skill-chaining', title: 'Chaining Skills' },
      { id: 'built-in-tools', title: 'Built-in Tools' },
    ]
  },
  {
    id: 'reasoning',
    title: 'How Agents Reason',
    items: [
      { id: 'multi-turn', title: 'Multi-Turn Conversations' },
      { id: 'knowledge-base', title: 'Knowledge Base' },
      { id: 'master-memory', title: 'Master Memory' },
    ]
  },
  {
    id: 'patterns',
    title: 'Patterns & Examples',
    items: [
      { id: 'pattern-coordinator', title: 'Slack → Email Coordinator' },
      { id: 'pattern-one-shot', title: 'On-Demand Assistant' },
      { id: 'pattern-async-reply', title: 'Async Reply Routing' },
      { id: 'catalog', title: 'Pre-Made Catalog' },
    ]
  },
  {
    id: 'monitoring',
    title: 'Monitoring',
    items: [
      { id: 'logs', title: 'Logs' },
      { id: 'escalation-logs', title: 'Escalation Logs' },
    ]
  },
  {
    id: 'api',
    title: 'API Reference',
    items: [
      { id: 'api-auth', title: 'Authentication' },
      { id: 'api-endpoints', title: 'Endpoints' },
      { id: 'api-webhooks', title: 'Incoming Webhooks' },
    ]
  },
];

function CodeBlock({ code, language }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="docs-codeblock">
      <div className="docs-codeblock-header">
        <span className="docs-codeblock-lang">{language}</span>
        <button className="docs-codeblock-copy" onClick={handleCopy}>
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="docs-codeblock-pre"><code>{code}</code></pre>
    </div>
  );
}

function TabbedCode({ tabs }) {
  const [active, setActive] = useState(0);
  return (
    <div className="docs-tabbed-code">
      <div className="docs-tabbed-header">
        {tabs.map((tab, i) => (
          <button key={i} className={`docs-tabbed-btn ${active === i ? 'active' : ''}`} onClick={() => setActive(i)}>{tab.label}</button>
        ))}
      </div>
      <CodeBlock code={tabs[active].code} language={tabs[active].language} />
    </div>
  );
}

function InfoCard({ icon: Icon, title, children, color }) {
  return (
    <div className="docs-info-card" style={{ '--card-color': color || '#f472b6' }}>
      <div className="docs-info-card-icon"><Icon size={18} /></div>
      <div className="docs-info-card-content">
        <h4>{title}</h4>
        <p>{children}</p>
      </div>
    </div>
  );
}

function MiniWorkspace({ type, name, tabs: tabData }) {
  const [activeTab, setActiveTab] = useState(0);
  return (
    <div className="docs-mini-workspace">
      <div className="docs-mw-titlebar">
        <div className="docs-mw-dots"><span className="dot r"></span><span className="dot y"></span><span className="dot g"></span></div>
        <span className="docs-mw-type">{type}</span>
        <span className="docs-mw-name">{name}</span>
      </div>
      <div className="docs-mw-tabs">
        {tabData.map((tab, i) => (
          <button key={i} className={`docs-mw-tab ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>{tab.label}</button>
        ))}
      </div>
      <div className="docs-mw-body">{tabData[activeTab]?.content}</div>
    </div>
  );
}

function StepList({ steps }) {
  return (
    <div className="docs-step-list">
      {steps.map((step, i) => (
        <div key={i} className="docs-step">
          <div className="docs-step-num">{i + 1}</div>
          <div className="docs-step-content">
            <h4>{step.title}</h4>
            <p>{step.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

const pages = {
  introduction: {
    title: 'Introduction to Aegent',
    render: () => (
      <>
        <p className="docs-lead">Aegent is an autonomous-agent platform. You build agents that watch for events you care about, reason on their own, take action with the tools you give them, and learn from every run.</p>

        <div className="docs-card-grid">
          <InfoCard icon={Radio} title="Listeners" color="#3b82f6">The agent's inputs. They watch a source — an inbox, a channel, a webhook, a schedule — and emit events when something happens.</InfoCard>
          <InfoCard icon={Code} title="Skills" color="#fbbf24">The agent's tools. Each skill is a Python class that does one thing — send a Slack message, query a database, call an API. The agent picks the right ones at runtime.</InfoCard>
          <InfoCard icon={Brain} title="Reasoning" color="#8b5cf6">When an event arrives, the agent plans a response, runs the skills it needs, and produces a final outcome. You shape its judgment with an Identity and a set of Behaviors.</InfoCard>
          <InfoCard icon={Database} title="Memory" color="#22c55e">Every run leaves a trace. The agent remembers what it has seen, what worked, and what didn't — so the next run is better than the last.</InfoCard>
        </div>

        <h2>How it's different from a chatbot</h2>
        <ul>
          <li><strong>Event-driven, not turn-driven.</strong> There is no chat window. Events arrive from your listeners; the agent runs end-to-end on its own. It can mark a thread as done or as waiting for a follow-up.</li>
          <li><strong>Skills run in isolation.</strong> Each skill call is sandboxed with only the credentials it needs. A misbehaving skill can't read another skill's secrets or affect the rest of the agent.</li>
          <li><strong>Reasoning is code.</strong> The agent doesn't just answer — it writes and executes code to call your tools and observe what happened. Multiple steps per event.</li>
          <li><strong>Threaded by content.</strong> Listeners tag each event with keys that identify the conversation it belongs to. Events with matching keys resume the same thread, even days apart.</li>
        </ul>
      </>
    )
  },

  quickstart: {
    title: 'Quick Start',
    render: () => (
      <>
        <p className="docs-lead">From zero to a working agent in under five minutes.</p>

        <StepList steps={[
          { title: 'Create an agent', desc: 'Sign in at /portal, click "New Agent", give it a name.' },
          { title: 'Fill in Identity', desc: 'Open Identity & Purpose. Set role ("Customer support for SaaS billing"), objective ("Resolve billing questions in two messages or escalate"), and success criteria. The agent uses these to shape every decision it makes.' },
          { title: 'Add Teaching', desc: 'Behavior & Teaching is for do/avoid lists and tone sliders. Plain text — write the rules in your own words. The agent will read and follow them.' },
          { title: 'Add a listener', desc: 'On the Workflow Builder, drop a listener (Email Inbox, Slack, Webhook, or paste your own). Fill credentials. Toggle Active.' },
          { title: 'Add skills', desc: 'Install from the pre-made catalog (Send Email, Slack Send, HTTP Request, etc.) or paste your own.' },
          { title: 'Configure escalation', desc: 'Set when the agent should hand off to a human and pick a delivery channel (email/Slack/webhook). The agent will use it whenever it decides escalation is needed.' },
          { title: 'Watch it run', desc: 'Open Logs to see every run with outcome and a one-line summary. Open Knowledge Base for richer per-run summaries.' },
        ]} />

        <h2>The minimum viable agent</h2>
        <p>If you just want to see one fire, install the Heartbeat listener and the Word Count skill from the catalog. Activate the listener. Within one poll cycle the agent will run once, leave a knowledge-base entry, and append a memory line. Both show up in the UI immediately.</p>
      </>
    )
  },

  'custom-agent-guide': {
    title: 'Build a Custom Agent',
    render: () => (
      <>
        <p className="docs-lead">An end-to-end walkthrough. We'll build a working agent from scratch — a <strong>GitHub PR triage bot</strong> that watches an inbox of pull-request notifications, summarizes each one, and posts a digest to Slack. Every section uses the real fields you'll edit in the UI, so you can follow along with your own agent at the same time.</p>

        <h2>What we're building</h2>
        <p>The agent:</p>
        <ul>
          <li>Polls a Gmail inbox for GitHub PR notification emails</li>
          <li>For each new PR, fetches the PR details from the GitHub API</li>
          <li>Generates a one-paragraph summary and risk note</li>
          <li>Posts the summary to a Slack channel</li>
          <li>Escalates if the PR touches sensitive paths (e.g. <code>infra/</code>, <code>migrations/</code>)</li>
        </ul>
        <p>It uses two listeners (Gmail polling, optional Slack reply for follow-ups), two custom skills (GitHub fetcher, Slack poster), and the built-in escalation tool. Everything except the Slack-poster credentials and your GitHub PAT is configured through the UI.</p>

        <div className="docs-card-grid">
          <InfoCard icon={Radio} title="Inputs" color="#3b82f6">One Gmail polling listener. Fires once per new email matching <code>from:notifications@github.com</code>.</InfoCard>
          <InfoCard icon={Code} title="Tools" color="#fbbf24">Two skills: <code>github_get_pr</code> (custom) and <code>slack_send</code> (catalog).</InfoCard>
          <InfoCard icon={Brain} title="Reasoning" color="#8b5cf6">The agent loops over its tools: fetch → summarize → post → escalate-if-needed → final answer.</InfoCard>
          <InfoCard icon={Users} title="Escalation" color="#22c55e">If the PR touches infra/migrations, hit a Slack webhook for human review.</InfoCard>
        </div>

        <h2>Step 1 — Create the empty agent</h2>
        <StepList steps={[
          { title: 'Open the Portal', desc: 'Go to /portal. Click "New Agent" in the top-right.' },
          { title: 'Pick a name', desc: 'Type "PR Triage" (or whatever you like). Hit Create. A fresh agent is provisioned with empty identity and behavior settings plus an example current_time skill.' },
          { title: 'Click into the agent', desc: 'You land on the Workflow Builder — a three-region canvas: catalog listeners on the left, your agent in the middle, catalog skills on the right.' },
        ]} />

        <h2>Step 2 — Write a sharp identity</h2>
        <p>Open the <strong>Identity & Purpose</strong> sidebar page. This is the single most important configuration step — the agent uses it on every decision, and the relevance filter uses it to decide which incoming events are worth running on.</p>

        <p>Fill in the fields. Here's a complete identity for our PR triage bot:</p>
        <CodeBlock language="json" code={`{
  "role": "GitHub pull-request triage assistant for the engineering team.",
  "industry": "Software development",
  "objective": "For each new pull-request notification email, fetch the PR details, write a one-paragraph plain-English summary highlighting what changes, post it to #pr-triage on Slack, and escalate if the PR touches infrastructure or database migrations.",
  "successDefinition": "A Slack message posted to #pr-triage containing PR title, author, summary, and any risk flags — within 2 minutes of the email arriving.",
  "escalationWhen": "The PR diff touches files under infra/, migrations/, .github/workflows/, or terraform/ — any change there needs a human reviewer before merge.",
  "escalationChannel": "slack",
  "escalationWho": "https://hooks.slack.com/services/T000/B000/XXXX",
  "escalationContext": "Tag @oncall in the Slack message. Include the PR URL, author, and the list of sensitive files touched.",
  "escalationSLA": "Acknowledge within 15 minutes during business hours."
}`} />

        <p>Tips that make a real difference:</p>
        <ul>
          <li><strong>Objective is your spec.</strong> Write it as a contract — what comes in, what the agent does, what comes out. Vague objectives ("be helpful with PRs") produce vague behavior.</li>
          <li><strong>Success definition is your verifier.</strong> Future-you will read the logs and want a clear pass/fail criterion. Be concrete: "Slack message posted with title, author, summary."</li>
          <li><strong>Escalation trigger should be objective, not subjective.</strong> "Touches <code>infra/</code> or <code>migrations/</code>" is testable; "looks risky" isn't.</li>
        </ul>

        <h2>Step 3 — Teach the agent how to behave</h2>
        <p>Open <strong>Behavior & Teaching</strong>. This is where you write the rules the agent must always follow and the things it must never do. Treat it like an employee handbook: short, concrete, action-oriented rules.</p>

        <CodeBlock language="json" code={`{
  "doGuidelines": "- Always include the PR URL as a link in the Slack message.\\n- Always mention the author by GitHub username (e.g. @alice-eng).\\n- Always state file count and net additions/deletions (e.g. \\"+142 -38 across 4 files\\").\\n- If a PR is from a bot (dependabot, renovate), use a one-line summary instead of a paragraph.\\n- Log a memory entry when you see repeat authors so you build context over time.",
  "avoidGuidelines": "- Never approve, merge, or comment on a PR — read-only.\\n- Never repost a PR you've already summarized (check master memory before posting).\\n- Don't guess at intent if the diff is unclear — call out the ambiguity in the summary.\\n- Don't include the full diff in Slack; link to GitHub instead.",
  "toneFormality": 35,
  "toneEnthusiasm": 40
}`} />

        <p>The two integers are purely informational — they don't change runtime behavior, but you can reference them in your guidelines ("match the team's casual tone — formality around 35/100"). Most teams leave them at 50.</p>

        <h2>Step 4 — Add a listener</h2>
        <p>Listeners are the agent's inputs. Open the <strong>Workflow Builder</strong> and drag <strong>Email Inbox</strong> from the left palette onto the canvas. Double-click the new node to open its workspace.</p>

        <p>Fill in the credentials and settings:</p>
        <table className="docs-table">
          <thead><tr><th>Field</th><th>Value</th></tr></thead>
          <tbody>
            <tr><td>IMAP Host</td><td><code>imap.gmail.com</code></td></tr>
            <tr><td>IMAP User</td><td><code>pr-triage@yourdomain.com</code></td></tr>
            <tr><td>App Password</td><td>(Gmail app password — not your account password)</td></tr>
            <tr><td>Mode</td><td><code>Unread only</code></td></tr>
            <tr><td>Poll Interval</td><td><code>60s</code></td></tr>
            <tr><td>Subject Filter</td><td><code>"[PR]"</code></td></tr>
          </tbody>
        </table>

        <p>Toggle <strong>Active</strong> on the listener. From now on, every new matching email becomes an event keyed on <code>["message_id"]</code> — meaning each PR notification is its own thread (no accidental conversation merging).</p>

        <p>If you wanted a polling listener the catalog doesn't cover, click <strong>+ New Listener</strong> and edit the starter template — a <code>def poll()</code> function that returns a list of artifacts:</p>
        <CodeBlock language="python" code={`import json, os, urllib.request

def main():
    token = os.environ["MY_API_TOKEN"]
    req = urllib.request.Request(
        "https://api.example.com/inbox",
        headers={"Authorization": f"Bearer {token}"},
    )
    with urllib.request.urlopen(req, timeout=10) as r:
        items = json.loads(r.read()).get("items", [])

    artifacts = [{
        "payload": {"source": "myapi", "id": item["id"], "body": item["text"]},
        "match_conversation_thread_by": ["id"],
    } for item in items]

    if artifacts:
        print(json.dumps(artifacts))

if __name__ == "__main__":
    main()`} />

        <h2>Step 5 — Add a skill from the catalog</h2>
        <p>From the right palette, drag <strong>Send Slack Message</strong> onto the canvas. Double-click to configure.</p>

        <CodeBlock language="json" code={`Credentials:
  SLACK_BOT_TOKEN  = xoxb-…
  SLACK_DEFAULT_CHANNEL = #pr-triage`} />

        <p>That's it for the catalog skill. The agent now has <code>slack_send(channel, text)</code> available as a callable tool and can invoke it during reasoning.</p>

        <h2>Step 6 — Write a custom skill</h2>
        <p>The catalog doesn't have a GitHub-PR-fetcher, so we'll write one. Click <strong>+ New Skill</strong> in the right palette. Name it <code>github_get_pr</code>. A new skill is scaffolded with three starter tabs in the Skill Workspace. Edit them as follows:</p>

        <h3>Script</h3>
        <CodeBlock language="python" code={`from smolagents import Tool
import os, json, urllib.request

class GitHubGetPRTool(Tool):
    name = "github_get_pr"
    description = "Fetch the title, author, body, file list, and additions/deletions for a GitHub PR by URL."
    inputs = {
        "url": {
            "type": "string",
            "description": "Full PR URL, e.g. https://github.com/acme/api/pull/4521"
        },
    }
    output_type = "object"

    def forward(self, url):
        # parse owner/repo/number out of the URL
        parts = url.rstrip("/").split("/")
        owner, repo, _, number = parts[-4], parts[-3], parts[-2], parts[-1]
        token = os.environ["GITHUB_TOKEN"]

        def fetch(path):
            req = urllib.request.Request(
                f"https://api.github.com/repos/{owner}/{repo}/{path}",
                headers={
                    "Authorization": f"Bearer {token}",
                    "Accept": "application/vnd.github+json",
                },
            )
            with urllib.request.urlopen(req, timeout=10) as r:
                return json.loads(r.read())

        pr = fetch(f"pulls/{number}")
        files = fetch(f"pulls/{number}/files")

        return {
            "title": pr["title"],
            "author": pr["user"]["login"],
            "body": (pr.get("body") or "")[:2000],
            "additions": pr["additions"],
            "deletions": pr["deletions"],
            "files": [f["filename"] for f in files[:50]],
            "html_url": pr["html_url"],
        }

if __name__ == "__main__":
    import sys
    kwargs = json.loads(sys.argv[1]) if len(sys.argv) > 1 else {}
    print(json.dumps(GitHubGetPRTool().forward(**kwargs)))`} />

        <h3>Metadata</h3>
        <CodeBlock language="json" code={`{
  "id": "github-get-pr",
  "slug": "github-get-pr",
  "name": "Get GitHub PR",
  "summary": "Fetch the title, author, body, file list, and diff stats for a GitHub PR.",
  "category": "skill",
  "type": "skill",
  "entrypoint": "main.py",
  "description_file": "description.md",
  "credentials": [
    {"name": "GITHUB_TOKEN", "label": "GitHub Personal Access Token", "type": "password"}
  ]
}`} />

        <h3>Description</h3>
        <CodeBlock language="markdown" code={`Fetches a single GitHub pull request and the list of files it touches.

**Returns** an object with: \`title\`, \`author\` (GitHub login), \`body\` (truncated to 2000 chars), \`additions\`, \`deletions\`, \`files\` (list of paths, capped at 50), \`html_url\`.

**Example**

\`\`\`python
pr = github_get_pr(url="https://github.com/acme/api/pull/4521")
# pr["author"] == "alice-eng"
# pr["files"] == ["api/server.py", "tests/test_server.py"]
\`\`\`

**Errors**: raises if the PR is private and the token lacks access, or if the URL is malformed.`} />

        <p>Paste your GitHub PAT into the credentials field in the Skill Workspace. Save. Aegent will introspect the script and confirm the tool was discovered — you'll see <code>github_get_pr(url: string) -&gt; object</code> next to a green check.</p>

        <h2>Step 7 — Escalation</h2>
        <p>We already filled <code>escalationWhen</code>, <code>escalationChannel</code>, and <code>escalationWho</code> in Step 2 — that's all the agent needs. The built-in <code>escalate(reason, context)</code> tool is always available; the agent decides when to call it based on the trigger text.</p>

        <p>Reinforce in your guidelines so the agent doesn't try to handle the sensitive PR itself:</p>
        <CodeBlock language="text" code={`Add to avoidGuidelines:
- If the PR touches infra/, migrations/, .github/workflows/, or terraform/,
  do NOT post a normal summary. Escalate with the PR URL, author, and the
  list of sensitive paths, and stop the run.`} />

        <h2>Step 8 — Trace one event end-to-end</h2>
        <p>Send yourself a test PR notification (or open a draft PR on a test repo). Within one poll cycle:</p>

        <ol>
          <li>The Email Inbox listener fires and emits one artifact: <code>{`{"payload": {...}, "match_conversation_thread_by": ["message_id"]}`}</code>.</li>
          <li>Aegent dedups the event and routes it to a conversation thread.</li>
          <li>The relevance filter reads your role + objective + event payload, decides it's on-topic, and starts the run.</li>
          <li>The agent calls <code>github_get_pr(url="https://github.com/acme/api/pull/4521")</code>. Aegent runs the skill in isolation with only <code>GITHUB_TOKEN</code> in scope and hands the JSON back.</li>
          <li>The agent inspects the PR's files, sees nothing sensitive, and posts the summary to <code>#pr-triage</code> via <code>slack_send(...)</code>.</li>
          <li>The agent logs a one-liner with <code>write_memory(...)</code> so future runs can see this PR was handled.</li>
          <li>The agent finishes the run. Aegent records a knowledge-base entry, appends to master memory, and writes the run log.</li>
        </ol>

        <p>Open the <strong>Logs</strong> page and you'll see the run with outcome and a one-line summary. Click to expand and see every step the agent produced, every observation it got back, and every tool call.</p>

        <h2>Step 9 — Iterate using logs and the knowledge base</h2>
        <p>The first time you run a real workload, expect surprises. Use the three feedback surfaces:</p>

        <table className="docs-table">
          <thead><tr><th>Surface</th><th>What it tells you</th><th>What to do with it</th></tr></thead>
          <tbody>
            <tr><td>Logs page</td><td>Step-by-step trace of every run. Tool errors, parse errors, model output.</td><td>If a skill fails repeatedly, fix the skill code or tighten its description.</td></tr>
            <tr><td>Knowledge Base</td><td>Per-run record of goal, outcome, resolution, and learnings.</td><td>Successful entries become reference patterns the agent leans on for similar future events.</td></tr>
            <tr><td>Master Memory</td><td>Append-only list of one-line facts.</td><td>Delete entries that are wrong or stale via the Master Memory page; the agent will stop using them on the next run.</td></tr>
          </tbody>
        </table>

        <h2>Common pitfalls</h2>
        <div className="docs-two-col">
          <div className="docs-dont-col">
            <h4>Gotchas</h4>
            <ul>
              <li><strong>Objective too narrow → events get filtered as off-topic.</strong> If the relevance filter consistently rejects events, broaden the objective or include the source name explicitly ("GitHub notifications, Slack mentions, escalation replies…").</li>
              <li><strong>Skill returns &gt; 100KB.</strong> Large outputs get truncated. Summarize inside the skill or write big payloads to a sandbox file and return the path.</li>
              <li><strong>Skill takes &gt; 60s.</strong> Skill calls are killed at 60s. Split into smaller calls or kick-off-and-poll patterns.</li>
              <li><strong>Threads not resuming.</strong> Verify the listener emits the same <code>match_conversation_thread_by</code> keys for both outbound and reply events. Test by sending a reply and checking it lands in the same <code>conv_xxx</code> thread.</li>
              <li><strong>Credentials show as undefined at runtime.</strong> Save the skill after editing — the credential schema is re-read whenever the Metadata changes.</li>
            </ul>
          </div>
          <div className="docs-do-col">
            <h4>Habits that pay off</h4>
            <ul>
              <li><strong>Make objectives a contract.</strong> Inputs → action → outputs. Future-you reads the logs against this.</li>
              <li><strong>Phrase rules with verbs.</strong> "Always include the PR URL" beats "PR links are important."</li>
              <li><strong>Tie hard rules to escalation.</strong> If a rule must never be broken, instruct the agent to escalate rather than guess.</li>
              <li><strong>Test the unhappy path.</strong> Send a malformed event, a permission-denied API response, an empty inbox. Watch how the agent recovers.</li>
              <li><strong>Read one full run trace end to end.</strong> The Logs detail view shows every step, every tool call, and every result — there's no substitute.</li>
            </ul>
          </div>
        </div>

        <h2>From here</h2>
        <p>You now have a working custom agent. Deepen specific areas with these references:</p>
        <ul>
          <li><strong>Identity & Purpose</strong> — every Identity field and how it shapes the agent's behavior.</li>
          <li><strong>Behavior & Teaching</strong> — writing do/avoid lists the agent actually respects.</li>
          <li><strong>How Listeners Work</strong> — the event contract, polling vs. webhook, threading keys.</li>
          <li><strong>Skill Format</strong> — the Tool shape and how skills get discovered.</li>
          <li><strong>Patterns & Examples</strong> — three end-to-end recipes for multi-source, multi-turn flows.</li>
        </ul>
      </>
    )
  },

  'creating-agents': {
    title: 'Creating an Agent',
    render: () => (
      <>
        <p className="docs-lead">Creating an agent gives you a fresh, isolated workspace under your account. Configuration is done through the sidebar pages — Identity, Teaching, Listeners, Skills, Escalation, Environment.</p>

        <StepList steps={[
          { title: 'Open the Portal', desc: 'Your Portal at /portal lists all agents you own. Each card shows name, listener count, and status.' },
          { title: 'Click New Agent', desc: 'Pick a name. The agent is provisioned immediately with empty identity and behavior settings.' },
          { title: 'Configure via the sidebar', desc: 'Inside an agent you have: Workflow Builder (visual layout), Identity, Teaching, Knowledge Files, Environment, Listeners, Escalation, Knowledge Base, Master Memory, Logs.' },
          { title: 'Activate listeners', desc: 'Listeners are inactive by default. Toggle Active on at least one to start firing events at the agent.' },
        ]} />

        <h2>The Workflow Builder</h2>
        <p>The Workflow Builder is the canvas view: listeners on the left, the agent in the middle, skills on the right. Click any node to open its full-page workspace. Bundled tools (web search, visit webpage) and built-in tools (read_memory, write_memory, escalate) are always present and aren't shown as separate nodes.</p>

        <h2>Configuration areas</h2>
        <div className="docs-card-grid">
          <InfoCard icon={Settings} title="Identity & Purpose" color="#f472b6">Role, industry, objective, success definition, escalation rules. The agent uses these on every run.</InfoCard>
          <InfoCard icon={BookOpen} title="Behavior & Teaching" color="#fb923c">Do/avoid lists and tone sliders. The agent reads and follows them.</InfoCard>
          <InfoCard icon={Layers} title="Knowledge Files" color="#8b5cf6">Reference docs the agent can look up via the file_read skill.</InfoCard>
          <InfoCard icon={Radio} title="Listeners" color="#3b82f6">Inputs. Polling or webhook. One Python script per listener.</InfoCard>
          <InfoCard icon={Code} title="Skills" color="#fbbf24">Tools the agent can call. One Tool class per skill, plus its credential schema.</InfoCard>
          <InfoCard icon={Users} title="Escalation" color="#22c55e">When to hand off and who to deliver to (email, Slack, webhook).</InfoCard>
        </div>
      </>
    )
  },

  identity: {
    title: 'Identity & Purpose',
    render: () => (
      <>
        <p className="docs-lead">Identity is the agent's contract with you. The agent reads it on every run. Be specific — vague identities produce vague agents.</p>

        <h2>Fields</h2>
        <table className="docs-table">
          <thead><tr><th>Field</th><th>What it does</th></tr></thead>
          <tbody>
            <tr><td><code>role</code></td><td>Who the agent is — sets the persona</td></tr>
            <tr><td><code>industry</code></td><td>Light domain context</td></tr>
            <tr><td><code>objective</code></td><td>The agent's mission — the most important field</td></tr>
            <tr><td><code>successDefinition</code></td><td>What "done" looks like for a run</td></tr>
            <tr><td><code>escalationWhen</code></td><td>Free text trigger: "Escalate when..."</td></tr>
            <tr><td><code>escalationWho</code></td><td>Channel-specific target (email address, Slack webhook URL, etc.)</td></tr>
            <tr><td><code>escalationChannel</code></td><td>One of: <code>email</code>, <code>slack</code>, <code>webhook</code></td></tr>
            <tr><td><code>escalationContext</code></td><td>Extra context attached to escalation payloads</td></tr>
            <tr><td><code>escalationSLA</code></td><td>Free-text SLA reference</td></tr>
          </tbody>
        </table>

        <h2>Writing a good objective</h2>
        <p>The objective shapes every decision the agent makes. It also drives the relevance filter — a broad objective lets more events through, a narrow one filters more aggressively.</p>
        <div className="docs-two-col">
          <div className="docs-do-col">
            <h4>Good</h4>
            <ul>
              <li>"Resolve customer billing questions in two messages, or escalate."</li>
              <li>"Triage inbound support emails: tag, summarize, route urgent ones to oncall."</li>
              <li>"Coordinate replies between Alice and Priya on deploy status."</li>
            </ul>
          </div>
          <div className="docs-dont-col">
            <h4>Bad</h4>
            <ul>
              <li>"Be helpful." (too vague — the relevance filter will let everything through)</li>
              <li>"Do whatever the user asks." (no anchor; agent has no priors)</li>
              <li>"Answer questions." (no scope, no success criterion)</li>
            </ul>
          </div>
        </div>
      </>
    )
  },

  teaching: {
    title: 'Behavior & Teaching',
    render: () => (
      <>
        <p className="docs-lead">Behavior is the rules the agent must always follow and the things it must never do. Treat it like an employee handbook — short, concrete rules.</p>

        <h2>Fields</h2>
        <ul>
          <li><strong>doGuidelines</strong> — what the agent should always do</li>
          <li><strong>avoidGuidelines</strong> — what the agent should never do</li>
          <li><strong>toneFormality</strong> — integer 0-100 (purely informational; you can reference it in guidelines)</li>
          <li><strong>toneEnthusiasm</strong> — integer 0-100 (purely informational)</li>
        </ul>

        <CodeBlock language="text" code={`Example doGuidelines:

- Greet customers by name when available
- Confirm the customer's issue before proposing a solution
- Always include a one-line summary at the end of every email
- Escalate for billing disputes over $500
- Log every commitment you make in master memory

Example avoidGuidelines:

- Never share internal pricing formulas
- Never promise features that aren't released
- Don't argue with frustrated customers — escalate instead
- Don't guess at technical specs; check or ask`} />

        <h2>How guidelines are enforced</h2>
        <p>The agent reads its behavior on every step. Enforcement is the agent following its instructions. Two things help:</p>
        <ul>
          <li><strong>Specificity beats verbosity.</strong> "Never promise a refund without checking the refund_policy skill first" works. "Be careful with money" doesn't.</li>
          <li><strong>Tie hard rules to escalation.</strong> If a rule must never be broken, phrase it so the agent escalates instead of guessing.</li>
        </ul>
      </>
    )
  },

  escalation: {
    title: 'Escalation',
    render: () => (
      <>
        <p className="docs-lead">When the agent can't safely finish a task, it calls the built-in <code>escalate</code> tool. That records the escalation and dispatches a notification to a configured channel.</p>

        <h2>How to trigger escalation</h2>
        <p>Two ways:</p>
        <ul>
          <li><strong>Implicit</strong> — Identity's <code>escalationWhen</code> tells the agent when to escalate. The agent decides based on the situation.</li>
          <li><strong>Explicit</strong> — In your guidelines, instruct the agent to escalate directly when a specific condition is hit.</li>
        </ul>
        <p>An escalation always ends the run with <code>outcome="escalated"</code> in the logs — that's the surface you'll see on the Logs page.</p>

        <h2>Delivery channels</h2>
        <table className="docs-table">
          <thead><tr><th>Channel</th><th>What's sent</th><th>Configure via</th></tr></thead>
          <tbody>
            <tr><td><code>email</code></td><td>Subject + body with reason and context</td><td><code>escalationWho</code> = email address. Uses Aegent SMTP env vars (<code>AEGENT_SMTP_HOST</code>, <code>AEGENT_SMTP_USER</code>, etc.) or falls back to the agent's <code>email_manager</code> skill credentials.</td></tr>
            <tr><td><code>slack</code></td><td>chat.postMessage to an incoming webhook URL</td><td><code>escalationWho</code> = Slack incoming webhook URL.</td></tr>
            <tr><td><code>webhook</code></td><td>JSON POST: <code>{`{agent_id, agent_name, reason, context, kind}`}</code></td><td><code>escalationWho</code> = arbitrary URL. Header <code>X-Aegent-Source: escalation</code>.</td></tr>
          </tbody>
        </table>

        <h2>Inspecting escalations</h2>
        <p>The Escalation Logs page shows every escalation: reason, context, dispatch attempt, and delivery status. Failed dispatches are recorded but the original escalation record is preserved.</p>
      </>
    )
  },

  environment: {
    title: 'Environment',
    render: () => (
      <>
        <p className="docs-lead">The Environment page is a file browser scoped to your agent's workspace. You can read and edit any file the agent reads or writes — useful for debugging.</p>

        <h2>What you can edit safely</h2>
        <ul>
          <li><strong>Identity and behavior files</strong> — same as the Identity / Teaching pages</li>
          <li><strong>Master memory file</strong> — remove a stale fact, add a permanent one. Append-only is conventional; manual edits are fine if you know the schema</li>
          <li><strong>Skill scripts</strong> — same as the Skill Workspace</li>
          <li><strong>Listener scripts</strong> — same as the Listener Workspace</li>
        </ul>

        <h2>What to leave alone</h2>
        <ul>
          <li><strong>Conversation history</strong> — editing breaks thread routing. Use the UI to clear waiting threads.</li>
          <li><strong>Knowledge base entries</strong> — read-only history. Delete entries if you must but don't rewrite them.</li>
          <li><strong>Listener dedup state</strong> — clearing it will cause already-handled events to fire again.</li>
        </ul>
      </>
    )
  },

  'listeners-overview': {
    title: 'How Listeners Work',
    render: () => (
      <>
        <p className="docs-lead">A listener is a Python script that emits one or more events for the agent to handle. No web framework, no endpoints, no SDK — just a function that prints JSON.</p>

        <h2>The contract</h2>
        <p>When invoked, the listener has access to its credentials as env vars and its settings as JSON in <code>AEGENT_SETTINGS</code>. It must print one or more JSON artifacts shaped like this:</p>

        <CodeBlock language="json" code={`{"payload": {"sender": "alice@…", "subject": "deploy?", "body": "is prod green?"},
 "match_conversation_thread_by": ["sender", "subject"]}`} />

        <p>That's the whole contract. Aegent takes care of:</p>
        <ul>
          <li>Deduplicating events (so the same heartbeat doesn't fire twice)</li>
          <li>Routing the event to the right conversation thread via <code>match_conversation_thread_by</code></li>
          <li>Running the relevance filter and the agent</li>
          <li>Persisting the result and updating the thread</li>
        </ul>

        <h2>Minimal example — heartbeat</h2>
        <CodeBlock language="python" code={`import json

def main():
    print(json.dumps({
        "payload": {
            "source": "heartbeat",
            "subject": "Scheduled check-in",
            "body": "Heartbeat: confirm everything looks healthy.",
        },
        "match_conversation_thread_by": ["source"],
    }))

if __name__ == "__main__":
    main()`} />

        <p>This fires once per poll. Because the payload is identical every time, dedup collapses it to a single event. To make it fire on every tick, add a timestamp to the payload so each artifact hashes differently.</p>
      </>
    )
  },

  'listener-format': {
    title: 'Event Format',
    render: () => (
      <>
        <p className="docs-lead">A listener prints either a single artifact or a JSON array of artifacts. Each artifact has exactly two top-level keys: <code>payload</code> and <code>match_conversation_thread_by</code>.</p>

        <h2>The artifact shape</h2>
        <CodeBlock language="json" code={`{
  "payload": {
    // Free-form. The whole object is what the agent reads when handling this event.
    // Common fields: source, subject, body, sender, message_id, thread_ts, etc.
    "source": "email",
    "from": "alice@example.com",
    "subject": "deploy status",
    "body": "is prod green?",
    "message_id": "<abc@example.com>"
  },
  "match_conversation_thread_by": ["from", "subject"]
}`} />

        <h3><code>payload</code></h3>
        <p>Whatever JSON-serializable structure you want. Aegent dedups identical payloads and shows the payload to the agent verbatim. Two artifacts with the same payload collapse to one event.</p>

        <h3><code>match_conversation_thread_by</code></h3>
        <p>An array of key names. Aegent reads those keys out of <code>payload</code>, joins their values, and uses that as the thread identifier. Two artifacts that produce the same identifier resume the same thread. See <a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('docs-goto', { detail: 'threading' })); }}>Conversation Threading</a> for routing rules.</p>

        <h2>Multiple events in one poll</h2>
        <p>Print a JSON array:</p>
        <CodeBlock language="python" code={`import json

events = [
    {"payload": {"source": "email", "id": "msg-1", "body": "..."},
     "match_conversation_thread_by": ["id"]},
    {"payload": {"source": "email", "id": "msg-2", "body": "..."},
     "match_conversation_thread_by": ["id"]},
]
print(json.dumps(events))`} />

        <h2>Returning nothing</h2>
        <p>Print an empty array <code>[]</code>, or nothing at all. Both are treated as "no events this poll."</p>
      </>
    )
  },

  'listener-types': {
    title: 'Polling vs. Webhook',
    render: () => (
      <>
        <p className="docs-lead">Two delivery modes. Both run the same Python script with the same contract — only the trigger differs.</p>

        <h2>Polling</h2>
        <p>Your listener runs on an interval you set (default 60s). The script does whatever work it needs (poll an inbox, hit an API, check a feed) and prints its findings. Use polling for sources that don't push: IMAP inboxes, REST APIs, RSS, databases.</p>

        <CodeBlock language="python" code={`import json, os, requests

def main():
    api_key = os.environ["API_KEY"]
    url = os.environ["API_URL"]
    r = requests.get(url, headers={"Authorization": f"Bearer {api_key}"})
    artifacts = []
    for item in r.json().get("results", []):
        artifacts.append({
            "payload": {"source": "myapi", "id": item["id"], "body": item["text"]},
            "match_conversation_thread_by": ["id"],
        })
    if artifacts:
        print(json.dumps(artifacts))

if __name__ == "__main__":
    main()`} />

        <p>Aegent dedups by payload hash (last ~500 IDs), so you don't need your own cursor unless you want to bound the API request.</p>

        <h2>Webhook</h2>
        <p>Aegent exposes <code>POST /webhook/&lt;agent_id&gt;/&lt;listener_id&gt;</code>. An <code>X-Aegent-Signature</code> header is verified on every request before the listener runs — see <strong>API → Incoming Webhooks</strong> for the signing format. On accepted requests, the body is passed to your listener as <code>AEGENT_EVENT</code> (a JSON string); your script reads it and prints artifacts.</p>

        <CodeBlock language="python" code={`import json, os

def main():
    body = json.loads(os.environ.get("AEGENT_EVENT") or "{}")
    artifact = {
        "payload": {"source": "stripe", **body},
        "match_conversation_thread_by": ["id"],
    }
    print(json.dumps(artifact))

if __name__ == "__main__":
    main()`} />

        <p>No need to verify a signature inside <code>main</code> — Aegent does that at the request boundary, so anything that reaches your listener has already been authenticated.</p>

        <h2>What about cron?</h2>
        <p>There's no separate cron mode. Use a polling listener with a longer interval, or check the current time inside <code>main</code> and skip emitting artifacts when you're not in the target window.</p>
      </>
    )
  },

  'listener-credentials': {
    title: 'Listener Credentials',
    render: () => (
      <>
        <p className="docs-lead">Listener credentials are declared in the listener's metadata and injected as environment variables when the script runs.</p>

        <h2>Declaring credentials</h2>
        <p>In the listener's metadata:</p>
        <CodeBlock language="json" code={`{
  "id": "email-inbox",
  "name": "Email Inbox",
  "category": "listener",
  "type": "listener",
  "entrypoint": "main.py",
  "listenerType": "polling",
  "credentials": [
    {"name": "IMAP_HOST", "label": "IMAP Host", "type": "text"},
    {"name": "IMAP_USER", "label": "Email Address", "type": "text"},
    {"name": "IMAP_PASSWORD", "label": "App Password", "type": "password"}
  ]
}`} />

        <p>The UI renders one input per credential. Values are stored encrypted on your agent, never in the listener script. At runtime they're injected into the listener's environment so your code reads them with <code>os.environ["IMAP_HOST"]</code>.</p>

        <h2>What else is in the env</h2>
        <table className="docs-table">
          <thead><tr><th>Variable</th><th>Contains</th></tr></thead>
          <tbody>
            <tr><td><code>AEGENT_SANDBOX</code></td><td>Path to your agent's workspace — safe to write scratch files under it</td></tr>
            <tr><td><code>AEGENT_SETTINGS</code></td><td>JSON-encoded listener settings (poll interval, mode flags, etc.)</td></tr>
            <tr><td><code>AEGENT_EVENT</code></td><td>(Webhook only) JSON-encoded request body</td></tr>
            <tr><td><code>AEGENT_MODE</code></td><td>Listener mode if your metadata declares modes</td></tr>
            <tr><td><code>AEGENT_CREATED_AT</code></td><td>ISO timestamp the listener was installed</td></tr>
          </tbody>
        </table>

        <h2>Storing state across polls</h2>
        <p>Write a file inside <code>$AEGENT_SANDBOX</code> — Aegent won't touch it. A common pattern is keeping a cursor file so each poll only fetches new items.</p>
      </>
    )
  },

  threading: {
    title: 'Conversation Threading',
    render: () => (
      <>
        <p className="docs-lead">Threading is how an agent maintains context across multiple events. The mechanism is content-addressed: each event names the keys whose values identify its thread. Events with matching values resume the same conversation.</p>

        <h2>The routing rule</h2>
        <p>For each artifact, Aegent:</p>
        <ol>
          <li>Reads <code>payload[key]</code> for every key in <code>match_conversation_thread_by</code></li>
          <li>Joins those values into a thread fingerprint</li>
          <li>Looks up the fingerprint in your agent's conversation history</li>
          <li>If found, appends the event to that thread; if not, creates a new thread</li>
        </ol>

        <h2>Choosing keys</h2>
        <table className="docs-table">
          <thead><tr><th>Source</th><th>Good thread keys</th></tr></thead>
          <tbody>
            <tr><td>Email</td><td><code>["message_id"]</code> for unique threads; <code>["from", "subject"]</code> for "Re:" chains</td></tr>
            <tr><td>Slack</td><td><code>["thread_ts"]</code> — every Slack reply in a thread shares the same <code>thread_ts</code></td></tr>
            <tr><td>Webhook</td><td><code>["conversation_id"]</code> or any stable ID the source provides</td></tr>
            <tr><td>One-shot</td><td><code>["ts"]</code> or anything unique per event — each event becomes its own thread</td></tr>
          </tbody>
        </table>

        <h2>Why this matters</h2>
        <p>When the agent resumes a thread, it has the conversation history — the original goal, what it did last time, and the new event together.</p>

        <p>If your keys don't match — say, an email reply strips "Re: " and you keyed on <code>["subject"]</code> — the reply lands in a brand-new thread with no context. Always test threading by sending a reply to an outbound event and confirming it routes to the same <code>conv_xxx</code> thread.</p>

        <h2>Waiting threads</h2>
        <p>When the agent finishes a turn while still expecting a follow-up — a reply, a callback, a scheduled trigger — the thread stays open. There's no TTL; a thread can wait days. When the next event arrives with matching keys, the agent resumes with full context.</p>
      </>
    )
  },

  'skills-overview': {
    title: 'How Skills Work',
    render: () => (
      <>
        <p className="docs-lead">A skill is a Python <code>Tool</code> class — a typed callable the agent can invoke during reasoning. The agent sees its signature, decides when to call it, and Aegent executes it in isolation.</p>

        <h2>Why typed tools?</h2>
        <p>Each Tool exposes a typed interface — name, description, inputs (with types and descriptions), output type. The agent sees this and calls tools by writing Python: <code>result = my_tool(arg="value")</code>. Typed interfaces produce cleaner, more reliable tool calls than free-text function declarations.</p>

        <h2>The shape</h2>
        <CodeBlock language="python" code={`from smolagents import Tool

class WordCountTool(Tool):
    name = "word_count"
    description = "Count the words and characters in a piece of text."
    inputs = {
        "text": {"type": "string", "description": "The text to measure."},
    }
    output_type = "object"

    def forward(self, text):
        return {"words": len(text.split()), "chars": len(text)}`} />

        <h2>What the agent sees</h2>
        <p>For the skill above, the agent sees a typed callable:</p>
        <CodeBlock language="text" code={`def word_count(text: string) -> object:
    """Count the words and characters in a piece of text.

    Args:
        text: The text to measure.
    """`} />
        <p>The agent calls it in its generated Python:</p>
        <CodeBlock language="python" code={`result = word_count(text="some text to measure")
# result = {"words": 4, "chars": 21}`} />

        <h2>What it returns</h2>
        <p>Whatever <code>forward()</code> returns is JSON-encoded and capped at 100KB before being handed back to the agent as a string observation. Big results — file contents, API responses — should be summarized inside the skill or written to a file in your agent's workspace and referenced by path.</p>
      </>
    )
  },

  'skill-format': {
    title: 'Skill Format',
    render: () => (
      <>
        <p className="docs-lead">A skill has three parts: a <strong>Script</strong> (the Tool), <strong>Metadata</strong> (name, credentials, schema), and a <strong>Description</strong> (extended description shown to the agent).</p>

        <h2>Script</h2>
        <CodeBlock language="python" code={`from smolagents import Tool
import os, json, smtplib
from email.message import EmailMessage

class SendEmailTool(Tool):
    name = "email_manager"
    description = "Send an email via SMTP."
    inputs = {
        "to":      {"type": "string", "description": "Recipient email address"},
        "subject": {"type": "string", "description": "Email subject"},
        "body":    {"type": "string", "description": "Email body (plain text)"},
    }
    output_type = "object"

    def forward(self, to, subject, body):
        msg = EmailMessage()
        msg["From"] = os.environ["FROM_ADDRESS"]
        msg["To"] = to
        msg["Subject"] = subject
        msg.set_content(body)

        with smtplib.SMTP_SSL(os.environ["SMTP_HOST"], int(os.environ.get("SMTP_PORT", "465"))) as s:
            s.login(os.environ["SMTP_USER"], os.environ["SMTP_PASSWORD"])
            s.send_message(msg)

        return {"sent": True, "to": to}

if __name__ == "__main__":
    import sys
    kwargs = json.loads(sys.argv[1]) if len(sys.argv) > 1 else {}
    print(json.dumps(SendEmailTool().forward(**kwargs)))`} />

        <h2>Metadata</h2>
        <CodeBlock language="json" code={`{
  "id": "send-email",
  "slug": "send-email",
  "name": "Send Email",
  "summary": "Sends an email via SMTP.",
  "category": "skill",
  "type": "skill",
  "credentials": [
    {"name": "SMTP_HOST", "label": "SMTP Host", "type": "text"},
    {"name": "SMTP_PORT", "label": "SMTP Port", "type": "text"},
    {"name": "SMTP_USER", "label": "SMTP User", "type": "text"},
    {"name": "SMTP_PASSWORD", "label": "SMTP Password", "type": "password"},
    {"name": "FROM_ADDRESS", "label": "From Address", "type": "text"}
  ]
}`} />

        <h2>Description</h2>
        <p>Markdown shown to the agent as the tool description. Keep it tight — the agent reads it on every run. Mention failure modes and edge cases ("returns an error if SMTP_HOST is unreachable"), and give one example invocation if the inputs are non-obvious.</p>

        <h2>Tool discovery</h2>
        <p>Aegent introspects every skill's script in isolation. It accepts any of:</p>
        <ul>
          <li>A module-level <code>tool = MyTool()</code> instance</li>
          <li>A <code>get_tool()</code> factory function returning a Tool instance</li>
          <li>A <code>TOOL_CLASS = MyTool</code> declaration</li>
          <li>The first <code>Tool</code> subclass declared in the module (most common — what the example above uses)</li>
        </ul>
        <p>Saving the script or changing credentials re-introspects automatically.</p>
      </>
    )
  },

  'skill-execution': {
    title: 'Skill Execution & Limits',
    render: () => (
      <>
        <p className="docs-lead">Every skill call runs in isolation. The agent never holds onto skill state between calls.</p>

        <h2>The execution model</h2>
        <ol>
          <li>The agent's generated code calls <code>my_skill(arg="value")</code></li>
          <li>Aegent runs your skill in an isolated context with only its declared credentials available</li>
          <li>Your <code>forward(**kwargs)</code> runs</li>
          <li>Its return value is JSON-encoded and capped at 100KB before being handed back to the agent as a string observation</li>
        </ol>

        <h2>Hard limits</h2>
        <table className="docs-table">
          <thead><tr><th>Limit</th><th>Value</th><th>What happens at the limit</th></tr></thead>
          <tbody>
            <tr><td>Skill timeout</td><td>60 seconds</td><td>The skill is killed; the agent observes <code>SKILL ERROR: &lt;name&gt; timed out</code></td></tr>
            <tr><td>Output cap (return)</td><td>100000 bytes</td><td>Truncated; agent sees first 100KB only</td></tr>
            <tr><td>Reasoning steps per event</td><td>~several</td><td>Loop exits; the agent must wrap up with whatever it has</td></tr>
            <tr><td>Concurrent skill calls</td><td>1 per run</td><td>The agent runs skills sequentially; chain calls inside one skill if you need parallelism</td></tr>
          </tbody>
        </table>

        <h2>Isolation properties</h2>
        <ul>
          <li><strong>No shared state.</strong> Each call is fresh; no module-level state survives between calls.</li>
          <li><strong>Credential scoping.</strong> A skill only sees its own declared credentials. Skill A cannot read skill B's secrets.</li>
          <li><strong>Filesystem access.</strong> Skills have a workspace under <code>AEGENT_SANDBOX</code> they can read and write freely.</li>
          <li><strong>Network access.</strong> Unrestricted. Skills make HTTP calls freely.</li>
        </ul>

        <h2>Long-running work</h2>
        <p>If a skill regularly approaches the 60s timeout, restructure it:</p>
        <ul>
          <li><strong>Kick off, then poll.</strong> Submit the job; return a job ID. A separate listener polls for completion and fires a follow-up event.</li>
          <li><strong>Batch into smaller calls.</strong> Process one item per call; the agent loops.</li>
        </ul>
      </>
    )
  },

  'skill-chaining': {
    title: 'Chaining Skills',
    render: () => (
      <>
        <p className="docs-lead">Skills chain inside a single agent run. The agent calls skill A, sees its return value, then calls skill B with that value as input. No listeners needed.</p>

        <h2>How it works</h2>
        <p>The agent reasons in multiple steps. At each step it writes Python that may call any tool. The return value becomes a Python variable the next step can use. A typical multi-skill turn looks like:</p>

        <CodeBlock language="python" code={`# Step 1: get the deploy status
status = http_request(method="GET", url="https://ci.example/last-build")

# Step 2: format and post to Slack
slack_send(channel="#deploys",
           text=f"Last build: {status['result']} at {status['finished_at']}")

# Step 3: log it for next time
write_memory(content=f"Reported deploy status {status['result']} on {status['finished_at']}")`} />

        <p>That's one event, one agent run, three skill calls, three model steps.</p>

        <h2>When chaining doesn't work</h2>
        <ul>
          <li><strong>Long waits.</strong> The 60s skill timeout means you can't sit inside a tool waiting hours for a human reply. Model that as a follow-up event from a second listener.</li>
          <li><strong>External dependencies you don't control.</strong> If skill B needs data that arrives asynchronously and might be delayed, end the run and let a follow-up event fire the next turn.</li>
          <li><strong>Cross-agent dependencies.</strong> Skills can't call other agents. If you need that, have the calling agent emit a webhook to the target agent's webhook listener.</li>
        </ul>

        <h2>Rule of thumb</h2>
        <p>Chain skills for synchronous request/response work. Use a second listener whenever you'd otherwise be sleeping inside a tool.</p>
      </>
    )
  },

  'built-in-tools': {
    title: 'Built-in Tools',
    render: () => (
      <>
        <p className="docs-lead">Every agent has these tools regardless of which skills are installed. They cover web access, memory, and human escalation.</p>

        <h2>Web tools</h2>
        <table className="docs-table">
          <thead><tr><th>Tool</th><th>Signature</th><th>Notes</th></tr></thead>
          <tbody>
            <tr><td><code>web_search</code></td><td><code>web_search(query: string)</code></td><td>DuckDuckGo-backed search. Returns a list of result snippets. Toggleable per-agent.</td></tr>
            <tr><td><code>visit_webpage</code></td><td><code>visit_webpage(url: string)</code></td><td>Fetches a page and returns its markdown-extracted text. Toggleable per-agent.</td></tr>
          </tbody>
        </table>

        <h2>Memory tools</h2>
        <table className="docs-table">
          <thead><tr><th>Tool</th><th>Signature</th><th>What it does</th></tr></thead>
          <tbody>
            <tr><td><code>read_memory</code></td><td><code>read_memory()</code></td><td>Returns the agent's most recent memory entries as a newline-separated list</td></tr>
            <tr><td><code>write_memory</code></td><td><code>write_memory(content: string)</code></td><td>Appends a durable fact to memory. The most recent entries are loaded for every future run.</td></tr>
          </tbody>
        </table>

        <p>The agent already has access to recent memory on every run — <code>read_memory()</code> is for fetching the rest. Use <code>write_memory</code> to add new facts that should survive to the next run.</p>

        <h2>Escalation</h2>
        <p>The agent can hand off to a human at any point. When it does, Aegent writes an escalation record and dispatches a notification through the channel configured in Identity (email, Slack, webhook). The run ends with <code>outcome="escalated"</code> in the logs. See <strong>Escalation</strong> in Agent Setup for delivery channel details.</p>
      </>
    )
  },

  'multi-turn': {
    title: 'Multi-Turn Conversations',
    render: () => (
      <>
        <p className="docs-lead">When an agent needs to wait for an external reply — a customer email, a Slack DM response — it finishes its turn and lets a follow-up event resume the conversation on the same thread.</p>

        <h2>The shape of a multi-turn flow</h2>
        <ol>
          <li><strong>Turn 1.</strong> Listener fires (e.g. user asks "ask Alice if deploy is green, then email Priya"). Agent runs, DMs Alice on Slack, and finishes its turn. The thread stays open.</li>
          <li><strong>Time passes.</strong> Could be 30 seconds. Could be 6 hours. The thread is held open.</li>
          <li><strong>Alice replies.</strong> A Slack reply listener polls for new messages and emits an artifact with <code>match_conversation_thread_by</code> values that resolve to the same thread.</li>
          <li><strong>Turn 2.</strong> Agent resumes with the original ask and Alice's reply both in its conversation context. It emails Priya, posts a confirmation back in the Slack thread, and closes the conversation.</li>
        </ol>

        <h2>What carries between turns</h2>
        <ul>
          <li><strong>Memory</strong> — durable facts written across runs. The agent records a one-line summary at the end of every run, so by turn 2 it can see what happened in turn 1.</li>
          <li><strong>Conversation history</strong> — recent messages on the thread. Inbound events and the agent's own turn summaries are both recorded.</li>
          <li><strong>Past successful resolutions</strong> — lessons from completed knowledge-base entries on this agent influence future runs.</li>
        </ul>

        <h2>The critical wiring</h2>
        <p>Multi-turn only works when the follow-up event resolves to the <em>same thread</em> as the outbound. Two things to verify:</p>
        <ul>
          <li><strong>Listener emits matching keys.</strong> If you sent the outbound on a Slack <code>thread_ts</code>, the reply listener must include <code>thread_ts</code> in the payload and in <code>match_conversation_thread_by</code>.</li>
          <li><strong>The relevance filter doesn't block the reply.</strong> The filter runs on every event, including replies. If the objective is too narrow, it may reject Alice's reply as off-topic. Test by sending a reply and watching the run log for a "skipped by relevance filter" entry.</li>
        </ul>

        <h2>No TTL</h2>
        <p>A waiting thread never expires. The agent will resume even after days. If you want time-bounded waits (e.g. "give up after 24 hours"), implement that explicitly — add a separate polling listener that fires a "timeout" event into the same thread when enough time has passed.</p>
      </>
    )
  },

  'knowledge-base': {
    title: 'Knowledge Base',
    render: () => (
      <>
        <p className="docs-lead">One entry per completed run. Each entry captures what happened — the goal, the outcome, a resolution summary, and the lessons the agent took away. Successful resolutions inform how the agent handles similar events in the future.</p>

        <h2>What's in an entry</h2>
        <p>Each entry records the goal of the run, the outcome (success / failed / escalated), a short resolution summary, topic keywords, and key learnings extracted from the run. You browse them on the <strong>Knowledge Base</strong> page; you don't write them yourself.</p>

        <h2>How it feeds back in</h2>
        <p>Past successful resolutions for this agent shape how it handles future runs. You don't have to do anything special — high-quality outcomes accrue automatically and become reference patterns.</p>

        <h2>Difference from master memory</h2>
        <table className="docs-table">
          <thead><tr><th></th><th>Knowledge base</th><th>Master memory</th></tr></thead>
          <tbody>
            <tr><td>Format</td><td>Structured per-run record</td><td>One-line text summary</td></tr>
            <tr><td>Use</td><td>"How did I solve a problem like this before?"</td><td>"What recently happened?"</td></tr>
            <tr><td>Written</td><td>End of every run</td><td>End of every run, plus on-demand via <code>write_memory</code></td></tr>
          </tbody>
        </table>
      </>
    )
  },

  'master-memory': {
    title: 'Master Memory',
    render: () => (
      <>
        <p className="docs-lead">An append-only list of durable facts. One line at the end of every run plus anything the agent explicitly writes via <code>write_memory</code>.</p>

        <h2>Shape</h2>
        <CodeBlock language="json" code={`[
  {
    "id": "<uuid>",
    "created_at": "2026-06-19T12:34:56Z",
    "source": "email",
    "summary": "Refunded $42 to Order #4521 per policy."
  },
  {
    "id": "<uuid>",
    "created_at": "2026-06-19T13:10:01Z",
    "source": "agent",
    "summary": "Acme account is on the Enterprise plan — refunds always go through finance@."
  }
]`} />

        <p>Newest entries are added last. <code>read_memory</code> returns them newest-first. Recent memory persists between runs and influences the agent's future decisions.</p>

        <h2>When to write to it explicitly</h2>
        <p>The agent calls <code>write_memory</code> when it learns something durable that will matter on future runs. Good examples:</p>
        <ul>
          <li>"Customer X always pays via wire transfer, not card."</li>
          <li>"The deploy@example.com address auto-replies; route deploy questions to ops@ instead."</li>
          <li>"Approved a $500 refund for Order #4521 with manager override from Priya."</li>
        </ul>
        <p>Bad examples — these belong in the KB or thread, not memory:</p>
        <ul>
          <li>"User said hi." (no durable signal)</li>
          <li>"I sent an email." (already in the thread + KB)</li>
          <li>"The current time is 14:32." (transient)</li>
        </ul>

        <h2>Editing manually</h2>
        <p>The Master Memory page in the UI lets you delete entries that turned out to be wrong. You can also edit memory directly via the Environment page. Just keep the array shape — entries are parsed strictly.</p>
      </>
    )
  },

  'pattern-coordinator': {
    title: 'Pattern: Slack → Email Coordinator',
    render: () => (
      <>
        <p className="docs-lead">A user types an instruction in a Slack channel. The agent DMs one person on Slack, waits for their reply, then emails a third person with what the first one said. This is the canonical multi-source, multi-turn pattern.</p>

        <h2>What you need</h2>
        <ul>
          <li><strong>Listener 1: Slack channel poller.</strong> Watches <code>#ops</code> for @-mentions. Emits one event per mention. <code>match_conversation_thread_by: ["thread_ts"]</code>.</li>
          <li><strong>Listener 2: Slack reply poller.</strong> Watches DM threads for new messages from people the bot has messaged. Emits events keyed on the same <code>thread_ts</code> as the original ask.</li>
          <li><strong>Listener 3 (optional): Email inbox poller.</strong> Only if the agent expects email replies as part of the flow.</li>
          <li><strong>Skill: slack_send_dm.</strong> DMs a Slack user by ID or email.</li>
          <li><strong>Skill: email_send.</strong> Sends email via SMTP.</li>
          <li><strong>Skill: slack_post_thread_reply.</strong> Posts a confirmation back to the originating Slack thread so the user knows the agent is working.</li>
        </ul>

        <h2>Identity</h2>
        <CodeBlock language="json" code={`{
  "role": "Coordinator that relays messages between a Slack channel, a Slack DM target, and an email recipient.",
  "objective": "When a user in #ops asks me to pass a message to someone, DM that person on Slack, wait for their reply, then email a summary to the stakeholder named in the request. Always confirm in the originating Slack thread when the email is sent.",
  "successDefinition": "Email sent to the stakeholder containing both the original ask and the responder's answer."
}`} />

        <h2>Flow</h2>
        <ol>
          <li>User in <code>#ops</code>: <em>"Ask Alice if the deploy is green, then let Priya know."</em></li>
          <li>Listener 1 emits an event. Agent runs.</li>
          <li>Agent DMs Alice: <code>slack_send_dm(to="alice@…", text="Is the deploy green?")</code></li>
          <li>Agent posts a holding reply in the originating thread: <code>slack_post_thread_reply(thread_ts=…, text="Asked Alice — waiting on her.")</code></li>
          <li>Agent finishes the turn. The thread stays open, waiting for Alice's reply.</li>
          <li>Some time later, Alice replies. Listener 2 emits an event with matching <code>thread_ts</code>. The thread resumes.</li>
          <li>Agent sees the original ask + Alice's reply. Calls <code>email_send(to="priya@…", subject="Deploy status from Alice", body="…")</code>.</li>
          <li>Posts a final confirmation back to <code>#ops</code> and closes the conversation.</li>
        </ol>

        <h2>Common pitfalls</h2>
        <ul>
          <li><strong>Mismatched thread keys.</strong> If Listener 1 keys on <code>["thread_ts"]</code> but Listener 2 keys on <code>["user", "channel"]</code>, the reply lands in a new thread with no context. Always test by sending a real reply.</li>
          <li><strong>Relevance filter blocks replies.</strong> If the objective is too narrow ("only handle deploy questions"), the relevance filter may reject Alice's reply. Phrase the objective to cover follow-ups.</li>
          <li><strong>Agent emails Priya immediately without waiting.</strong> Happens when the agent can't tell it's mid-flow. Reinforce in <code>doGuidelines</code>: "Never send the stakeholder email before the responder has replied."</li>
        </ul>
      </>
    )
  },

  'pattern-one-shot': {
    title: 'Pattern: On-Demand Assistant',
    render: () => (
      <>
        <p className="docs-lead">Wire a Slack channel as the input and the agent's only job is to do whatever the user types. Each message is its own task.</p>

        <h2>Listener</h2>
        <p>Slack channel poller, keyed by <code>["ts"]</code> — every top-level message becomes its own thread, so each ask is a fresh one-shot task. (Use <code>["thread_ts"]</code> instead if you want replies in a Slack thread to continue the same conversation.)</p>

        <h2>Identity</h2>
        <CodeBlock language="json" code={`{
  "role": "Personal assistant in #my-channel.",
  "objective": "Execute ad-hoc tasks the user assigns me in #my-channel. Each message is an instruction; do what it says using my skills, then post a one-line confirmation back to the originating Slack thread.",
  "successDefinition": "Task completed and a confirmation posted to the channel."
}`} />

        <h2>Skills</h2>
        <p>A generic toolkit — whatever you might want to ask the assistant to do. Common starter kit:</p>
        <ul>
          <li><code>slack_send_dm</code> and <code>slack_post_thread_reply</code> for sending Slack messages</li>
          <li><code>email_send</code> for outbound email</li>
          <li><code>http_request</code> for hitting arbitrary APIs</li>
          <li><code>web_search</code> and <code>visit_webpage</code> are already built-in</li>
          <li><code>calendar_create_event</code> if you have a Google / Outlook integration</li>
        </ul>

        <h2>doGuidelines</h2>
        <p>Make sure the agent posts a confirmation back so the user sees something happened:</p>
        <CodeBlock language="text" code={`- Always post a one-line confirmation back to the originating Slack thread when the task is complete.
- If you can't complete a request, post why in the thread before escalating.
- If a task needs multiple steps, post a brief progress update after each significant skill call.`} />

        <h2>Why this works</h2>
        <p>A single Slack message like "DM Alice about the deploy then email Priya" runs as one agent invocation. Skills chain inside the loop. If something needs to wait (Alice taking hours to reply), the email-reply pattern from the previous section kicks in.</p>
      </>
    )
  },

  'pattern-async-reply': {
    title: 'Pattern: Async Reply Routing',
    render: () => (
      <>
        <p className="docs-lead">Agent emails one person, that person replies hours or days later, agent reads the reply and acts. The pattern that makes Aegent more than a one-shot bot.</p>

        <h2>The wiring</h2>
        <ol>
          <li><strong>Outbound listener</strong> fires (any source — Slack, webhook, scheduled). Agent runs and calls <code>email_send(to="john@…", subject="Approval needed for #4521", …)</code>. Finishes the turn while the thread stays open for John's reply.</li>
          <li><strong>Email-read listener</strong> polls the inbox via IMAP. Each new message becomes one event. Its <code>match_conversation_thread_by</code> uses email threading headers — <code>["In-Reply-To"]</code> or <code>["References"]</code> or a fallback like <code>["from", "subject_normalized"]</code>.</li>
          <li>When John replies, the email-read listener emits an event whose thread fingerprint matches the original outbound. The agent resumes with full context.</li>
        </ol>

        <h2>Email threading keys — what actually works</h2>
        <table className="docs-table">
          <thead><tr><th>Key choice</th><th>Reliability</th><th>Notes</th></tr></thead>
          <tbody>
            <tr><td><code>["Message-ID"]</code></td><td>Unique per message</td><td>Each email is its own thread. Use when you don't want replies grouped.</td></tr>
            <tr><td><code>["In-Reply-To"]</code></td><td>Standard reply header</td><td>Reply's <code>In-Reply-To</code> = original's <code>Message-ID</code>. The right choice if the outbound's <code>Message-ID</code> is stored as the thread key.</td></tr>
            <tr><td><code>["References"]</code> (first)</td><td>Long-thread tracking</td><td>The root <code>Message-ID</code> of the thread. Persists across multiple replies.</td></tr>
            <tr><td><code>["from", "subject"]</code></td><td>Fragile</td><td>Works if no one alters "Re:" prefixes. Some clients reorder To/Cc.</td></tr>
          </tbody>
        </table>

        <p>Recommended: when the agent sends the outbound, the email_send skill should print the new <code>Message-ID</code> back to the agent. Have the agent <code>write_memory("Awaiting reply on Message-ID: &lt;abc@…&gt; from John")</code> if you want a paper trail.</p>

        <h2>Bidirectional flows</h2>
        <p>You can extend this to N participants. The agent emails A; A replies; the email-read listener fires; the agent emails B with A's answer; B replies; another event resumes; and so on. As long as each new event's thread key resolves to the same thread, the agent sees the entire history.</p>

        <h2>Verify the wiring before relying on it</h2>
        <p>Run an end-to-end test: send an outbound, reply to it from another account, watch the listener log. The reply should land in the same <code>conv_xxx</code> thread file as the outbound. If it lands in a new thread, your keys are wrong.</p>
      </>
    )
  },

  catalog: {
    title: 'Pre-Made Catalog',
    render: () => (
      <>
        <p className="docs-lead">Ready-to-install listeners and skills. Install from the Workflow Builder, configure credentials, activate.</p>

        <h2>Listeners</h2>
        <ul>
          <li><strong>Heartbeat</strong> — One scheduled check-in per poll. Useful as a smoke test or for periodic agent self-check-ins.</li>
          <li><strong>Email Inbox</strong> — Polls IMAP for new mail. Modes: all messages, unread only.</li>
          <li><strong>Slack Messages</strong> — Watches a Slack channel. Modes: all messages, mentions only.</li>
          <li><strong>Microsoft Teams Messages</strong> — Watches a Teams channel via Graph API.</li>
          <li><strong>RSS Feed</strong> — Monitors any RSS/Atom feed for new entries.</li>
          <li><strong>Webhook</strong> — Receives HTTP POSTs at <code>/webhook/&lt;agent_id&gt;/&lt;listener_id&gt;</code>.</li>
          <li><strong>Scheduled Trigger</strong> — Fires a polling listener on a fixed interval with a static payload.</li>
        </ul>

        <h2>Skills</h2>
        <ul>
          <li><strong>Send Email</strong> — SMTP outbound mail.</li>
          <li><strong>Send Slack Message</strong> — Posts to channels or DMs via Slack Web API.</li>
          <li><strong>Database Query</strong> — PostgreSQL read or write.</li>
          <li><strong>HTTP Request</strong> — Generic REST call.</li>
          <li><strong>Web Search</strong> — Built-in. DuckDuckGo-backed.</li>
          <li><strong>File Read/Write</strong> — Read or write files within the agent sandbox.</li>
          <li><strong>Word Count</strong> — Example skill. Counts words and characters in a string.</li>
          <li><strong>Current Time</strong> — Example skill. Returns the current UTC timestamp.</li>
        </ul>

        <h2>Installing from the catalog</h2>
        <ol>
          <li>Open Workflow Builder for your agent.</li>
          <li>Click "Add Listener" or "Add Skill" to open the catalog.</li>
          <li>Pick an item, click Install. The folder is copied into your agent's sandbox.</li>
          <li>Open the installed item to fill in credentials. Each item declares what it needs.</li>
          <li>Toggle Active (for listeners). Skills are always available to the agent once installed.</li>
        </ol>

        <h2>Forking a catalog item</h2>
        <p>Catalog items are copied into your agent when installed. Edit the script freely afterward — the original in the catalog is untouched. Use this when a catalog skill is 90% right but needs one custom field.</p>
      </>
    )
  },

  logs: {
    title: 'Logs',
    render: () => (
      <>
        <p className="docs-lead">The Logs page shows every run your agent has done, plus per-listener logs. One line per event, expandable to a full step trace.</p>

        <h2>What's in the run log</h2>
        <p>Each line is one agent run:</p>
        <CodeBlock language="text" code={`2026-06-19T12:34:56Z run thread=conv_abc123 outcome=success: Refunded $42 to Order #4521 per policy.
2026-06-19T12:35:14Z run thread=conv_def456 skipped by relevance filter: spam newsletter, off-topic
2026-06-19T12:36:01Z run thread=conv_ghi789 retrying after 3 tool-call errors`} />

        <p>Fields per line: timestamp, thread ID, outcome, one-line summary.</p>

        <h2>Listener logs</h2>
        <p>Each listener has its own per-listener log: ISO timestamp, number of artifacts emitted, and which threads they routed to.</p>
        <CodeBlock language="text" code={`- 2026-06-19T12:20:48Z event (1 msg) -> thread conv_abc123
- 2026-06-19T12:21:48Z event (2 msgs) -> thread conv_def456, conv_ghi789
- 2026-06-19T12:22:48Z poll empty`} />

        <h2>Skill output</h2>
        <p>Skills don't have a dedicated log. Their stdout and stderr are captured per-call, included in the agent's run transcript, and surfaced in the run's KB entry timeline. Use <code>print()</code> for debug output during development; remove or downgrade before production since every print contributes to the 100KB output cap.</p>

        <h2>Filtering and searching</h2>
        <p>The UI provides text search, source filter (per-listener, per-skill), level filter (success / failed / escalated), and a live tail. Click any entry to expand and see the full step memory: each model call, each observation, each tool error.</p>
      </>
    )
  },

  'escalation-logs': {
    title: 'Escalation Logs',
    render: () => (
      <>
        <p className="docs-lead">Every call to <code>escalate</code> writes a markdown file to <code>escalations/</code>. The Escalation Logs page reads them and shows delivery status.</p>

        <h2>Record shape</h2>
        <p>One file per escalation, named <code>&lt;timestamp&gt;_&lt;slug&gt;.md</code>:</p>
        <CodeBlock language="markdown" code={`# Escalation 2026-06-19T12:36:01Z

**Reason:** Customer requested refund over $1000 — exceeds my approval limit.

**Context:**

Order #4521, $1499.00, paid via card ending 4242 on 2026-06-15.
Customer email thread: …
Policy says refunds over $1000 require finance team approval.`} />

        <h2>Dispatch logs</h2>
        <p>When the configured channel is hit, a <code>dispatch_&lt;timestamp&gt;.md</code> alongside the escalation records the attempt:</p>
        <ul>
          <li><strong>email</strong>: <code>dispatched=true</code> and the SMTP message ID, or the error if delivery failed.</li>
          <li><strong>slack</strong>: webhook URL hit, HTTP status, response body.</li>
          <li><strong>webhook</strong>: target URL, HTTP status, response body.</li>
        </ul>
        <p>If dispatch fails, the escalation record is still authoritative — you can replay delivery manually.</p>

        <h2>Manually closing</h2>
        <p>There's no "resolve" state on escalations — they're a notification log, not a ticketing system. Delete the markdown file if you want to clear the entry from the UI.</p>
      </>
    )
  },

  'api-auth': {
    title: 'API Authentication',
    render: () => (
      <>
        <p className="docs-lead">Aegent uses signed JWTs. Sign in to get a token, then send it on every API request as <code>Authorization: Bearer &lt;token&gt;</code>.</p>

        <h2>Sign in</h2>
        <TabbedCode tabs={[
          { label: 'JavaScript', language: 'javascript', code: `const r = await fetch('/auth/signin', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ login: 'you@example.com', password: '…' }),
});
const { token } = await r.json();` },
          { label: 'cURL', language: 'bash', code: `curl -X POST https://your-domain/auth/signin \\
  -H "Content-Type: application/json" \\
  -d '{"login":"you@example.com","password":"…"}'` },
        ]} />

        <h2>Sign up</h2>
        <CodeBlock language="bash" code={`curl -X POST https://your-domain/auth/signup \\
  -H "Content-Type: application/json" \\
  -d '{"email":"you@example.com","username":"you","password":"…"}'`} />
        <p>Returns the same shape: <code>{`{token, user: {…}}`}</code>. The token is a signed JWT (HS256) containing <code>uid</code>, <code>username</code>, <code>email</code>, and <code>iat</code>.</p>

        <h2>Calling protected endpoints</h2>
        <CodeBlock language="bash" code={`curl https://your-domain/agents \\
  -H "Authorization: Bearer <jwt>"`} />

        <h2>What's public</h2>
        <ul>
          <li><code>GET /docs</code> — this documentation</li>
          <li><code>POST /auth/signup</code>, <code>POST /auth/signin</code></li>
          <li><code>POST /webhook/&lt;agent_id&gt;/&lt;listener_id&gt;</code> — webhook listener endpoint. Implement your own signature check inside the listener for production.</li>
          <li>Static pages: landing, legal</li>
        </ul>
        <p>Everything else requires the <code>Authorization: Bearer</code> header.</p>
      </>
    )
  },

  'api-endpoints': {
    title: 'API Endpoints',
    render: () => (
      <>
        <p className="docs-lead">All endpoints expect JSON request bodies and return JSON responses. Authenticate with <code>Authorization: Bearer &lt;jwt&gt;</code>.</p>

        <h2>Agents</h2>
        <table className="docs-table">
          <thead><tr><th>Method</th><th>Path</th><th>Purpose</th></tr></thead>
          <tbody>
            <tr><td>GET</td><td><code>/agents</code></td><td>List agents owned by the caller</td></tr>
            <tr><td>POST</td><td><code>/agents</code></td><td>Create an agent (body: agent document)</td></tr>
            <tr><td>GET</td><td><code>/agents/&lt;agent_id&gt;</code></td><td>Read full agent config</td></tr>
            <tr><td>PUT</td><td><code>/agents/&lt;agent_id&gt;</code></td><td>Patch agent config (partial document)</td></tr>
            <tr><td>DELETE</td><td><code>/agents/&lt;agent_id&gt;</code></td><td>Delete agent and sandbox</td></tr>
          </tbody>
        </table>

        <h2>Listeners</h2>
        <table className="docs-table">
          <thead><tr><th>Method</th><th>Path</th><th>Purpose</th></tr></thead>
          <tbody>
            <tr><td>GET</td><td><code>/agents/&lt;agent_id&gt;/listeners</code></td><td>List listeners on an agent</td></tr>
            <tr><td>POST</td><td><code>/agents/&lt;agent_id&gt;/listeners</code></td><td>Install a listener (body: metadata + script)</td></tr>
            <tr><td>PUT</td><td><code>/agents/&lt;agent_id&gt;/listeners/&lt;listener_id&gt;</code></td><td>Update listener code, settings, or credentials</td></tr>
            <tr><td>DELETE</td><td><code>/agents/&lt;agent_id&gt;/listeners/&lt;listener_id&gt;</code></td><td>Remove listener</td></tr>
            <tr><td>POST</td><td><code>/webhook/&lt;agent_id&gt;/&lt;listener_id&gt;</code></td><td>Push an event to a webhook listener (no auth)</td></tr>
          </tbody>
        </table>

        <h2>Skills</h2>
        <table className="docs-table">
          <thead><tr><th>Method</th><th>Path</th><th>Purpose</th></tr></thead>
          <tbody>
            <tr><td>GET</td><td><code>/agents/&lt;agent_id&gt;/skills</code></td><td>List skills</td></tr>
            <tr><td>POST</td><td><code>/agents/&lt;agent_id&gt;/skills</code></td><td>Install a skill</td></tr>
            <tr><td>PUT</td><td><code>/agents/&lt;agent_id&gt;/skills/&lt;skill_id&gt;</code></td><td>Update skill</td></tr>
            <tr><td>POST</td><td><code>/agents/&lt;agent_id&gt;/skills/&lt;skill_id&gt;/run</code></td><td>Invoke a skill directly with JSON params (testing)</td></tr>
            <tr><td>DELETE</td><td><code>/agents/&lt;agent_id&gt;/skills/&lt;skill_id&gt;</code></td><td>Uninstall skill</td></tr>
          </tbody>
        </table>

        <h2>Memory and knowledge</h2>
        <table className="docs-table">
          <thead><tr><th>Method</th><th>Path</th><th>Purpose</th></tr></thead>
          <tbody>
            <tr><td>GET</td><td><code>/agents/&lt;agent_id&gt;/memory</code></td><td>Read master memory entries</td></tr>
            <tr><td>DELETE</td><td><code>/agents/&lt;agent_id&gt;/memory/&lt;entry_id&gt;</code></td><td>Remove a memory entry</td></tr>
            <tr><td>GET</td><td><code>/agents/&lt;agent_id&gt;/knowledge</code></td><td>List knowledge-base entries</td></tr>
            <tr><td>GET</td><td><code>/agents/&lt;agent_id&gt;/conversations</code></td><td>List conversation threads</td></tr>
            <tr><td>GET</td><td><code>/agents/&lt;agent_id&gt;/logs</code></td><td>Read run logs</td></tr>
            <tr><td>GET</td><td><code>/agents/&lt;agent_id&gt;/escalations</code></td><td>Read escalation records</td></tr>
          </tbody>
        </table>
      </>
    )
  },

  'api-webhooks': {
    title: 'Incoming Webhooks',
    render: () => (
      <>
        <p className="docs-lead">External services push events to your agent via webhook URLs. One URL per webhook listener; every request must be signed.</p>

        <h2>URL format</h2>
        <CodeBlock language="text" code={`POST https://your-domain/webhook/<agent_id>/<listener_id>`} />
        <p>You'll find both IDs in the Listener Workspace after creating a webhook listener. The URL is not a secret — it can leak via screenshots, browser history, or third-party dashboards — so it can't act as auth on its own.</p>

        <h2>Signature is required</h2>
        <p>Every request must include <code>X-Aegent-Signature: sha256=&lt;hex&gt;</code> where <code>&lt;hex&gt;</code> is the HMAC-SHA256 of the raw request body using the listener's <code>webhook_secret</code>. Requests without a valid signature get <code>401</code> and the listener never runs.</p>

        <p>The secret is generated for you when the listener is created and returned in the listener API response. The Listener Workspace surfaces it as a "Webhook Secret" field — copy it once and paste it into whatever you're connecting.</p>

        <h2>Sending an event</h2>
        <CodeBlock language="bash" code={`SECRET="your-listener-webhook-secret"
BODY='{"event":"order.created","data":{"order_id":"789","total":99.99}}'
SIG="sha256=$(printf '%s' "$BODY" | openssl dgst -sha256 -hmac "$SECRET" -hex | awk '{print $2}')"

curl -X POST https://your-domain/webhook/abc123/listener_456 \\
  -H "Content-Type: application/json" \\
  -H "X-Aegent-Signature: $SIG" \\
  -d "$BODY"`} />

        <p>Or from Python:</p>
        <CodeBlock language="python" code={`import hmac, hashlib, json, requests

SECRET = "your-listener-webhook-secret"
body = json.dumps({"event": "order.created", "data": {"order_id": "789"}}).encode()
sig = "sha256=" + hmac.new(SECRET.encode(), body, hashlib.sha256).hexdigest()

requests.post(
    "https://your-domain/webhook/abc123/listener_456",
    data=body,
    headers={"Content-Type": "application/json", "X-Aegent-Signature": sig},
)`} />

        <p>On accepted requests, the verified body is passed to your listener as <code>AEGENT_EVENT</code> (a JSON string). Your listener parses it and emits artifacts exactly like a polling listener would — you no longer need to verify the signature inside the listener; Aegent does it for you.</p>

        <h2>Sign the bytes that go on the wire</h2>
        <p>Compute the HMAC over the <em>raw request body</em> exactly as sent — not over a re-serialized JSON object. If your HTTP client reformats the body (whitespace, key ordering, escaping), the signature won't match. Most senders solve this by serializing once and signing the resulting bytes; that's the pattern in the examples above.</p>

        <h2>Connecting fixed-scheme providers (Stripe, GitHub, Slack, etc.)</h2>
        <p>Senders like Stripe, GitHub, and Slack sign their webhooks with <strong>their own</strong> secret in <strong>their own</strong> header (<code>Stripe-Signature</code>, <code>X-Hub-Signature-256</code>, <code>X-Slack-Signature</code>, etc.). They won't sign with your Aegent listener secret, so you can't point them at the webhook URL directly.</p>

        <p>Two clean ways to wire them up:</p>
        <ul>
          <li><strong>Polling listener instead of webhook.</strong> If the provider has an API you can poll (e.g. GitHub's REST API for recent events, Stripe's <code>events.list</code>), a polling listener is simpler and skips the signing problem entirely. This is the recommended path for most cases.</li>
          <li><strong>Thin signing proxy.</strong> Stand up a small adapter (one Cloud Function / Lambda / Cloudflare Worker) that receives the provider's webhook, verifies <em>their</em> signature with <em>their</em> secret, then re-signs the body with your Aegent listener secret and forwards it to your webhook URL. Acts as the trust boundary between their signing scheme and yours.</li>
        </ul>

        <h2>Status codes</h2>
        <ul>
          <li><code>200</code> — accepted; the listener ran and any artifacts were routed.</li>
          <li><code>401</code> — missing or invalid <code>X-Aegent-Signature</code>. The listener did not run.</li>
          <li><code>404</code> — agent or listener doesn't exist. The response is the same for both so callers can't enumerate which agent IDs are valid.</li>
          <li><code>500</code> — the listener script crashed. Most senders will retry on non-2xx with backoff; if you want to reject without retry, make the listener print <code>[]</code> and return 200.</li>
        </ul>
      </>
    )
  },
};

export default function Docs() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('introduction');
  const [expandedSections, setExpandedSections] = useState(
    sections.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
  );

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const currentPage = pages[activeSection];

  return (
    <div className="docs-page">
      <div className="docs-frame">
        <div className="frame-border"></div>

        <nav className="docs-nav">
          <div className="nav-left">
            <button className="nav-logo" onClick={() => navigate('/')} aria-label="Go to homepage">
              <img src="/akakai-logo.png" alt="akakAI" className="nav-brand-logo" />
            </button>
          </div>
          <div className="nav-center">
            <span className="nav-title">Documentation</span>
          </div>
          <div className="nav-right">
            <button className="nav-btn nav-btn-ghost" onClick={() => navigate('/auth')}>Sign In</button>
            <button className="nav-btn nav-btn-primary" onClick={() => navigate('/auth')}>Get Started</button>
          </div>
        </nav>

        <div className="docs-layout">
          <div className="docs-mobile-nav">
            <select
              className="docs-mobile-select"
              value={activeSection}
              onChange={(e) => setActiveSection(e.target.value)}
            >
              {sections.map(section => (
                <optgroup key={section.id} label={section.title}>
                  {section.items.map(item => (
                    <option key={item.id} value={item.id}>{item.title}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <aside className="docs-sidebar" role="navigation" aria-label="Documentation sections">
            {sections.map(section => (
              <div key={section.id} className="sidebar-section">
                <button
                  className="sidebar-section-title"
                  onClick={() => toggleSection(section.id)}
                  aria-expanded={expandedSections[section.id]}
                >
                  {expandedSections[section.id] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  {section.title}
                </button>
                {expandedSections[section.id] && section.items.map(item => (
                  <button key={item.id} className={`sidebar-item ${activeSection === item.id ? 'active' : ''}`} onClick={() => setActiveSection(item.id)}>
                    {item.title}
                  </button>
                ))}
              </div>
            ))}
          </aside>

          <main className="docs-content" key={activeSection}>
            <article className="docs-article">
              <h1>{currentPage?.title || 'Coming Soon'}</h1>
              <div className="article-body">
                {currentPage?.render ? currentPage.render() : <p>This documentation section is being written. Check back soon.</p>}
              </div>
            </article>
          </main>
        </div>
      </div>
    </div>
  );
}
